"use client"

import { useAuth } from "@/contexts/auth-context"
import { env } from "@/lib/env"
import { getBasePathUrl } from "@/lib/base-path"
import { requestTokenDueToAuthFailure } from "@/lib/app-messaging"
import { tokenManager } from "@/lib/token-manager"
import { CalculatorFormData, CalculatorProvince, CostCalculationResult } from "@/lib/types/calculator"

// API endpoints
export const API_ENDPOINTS = {
  // Main API
  MAIN: {
    BASE_URL: env.apiUrl,
    // Add specific endpoints here
    USERS: "/users",
    // ...
  },
  // Admin Service API
  ADMIN: {
    BASE_URL: env.adminServiceApiEndpoint,
    // Add specific endpoints here
    SETTINGS: "/settings",
    // ...
  },
}

// Log API endpoints for debugging
if (typeof window !== "undefined") {
  console.log("API_ENDPOINTS:", API_ENDPOINTS)
}

// Custom hook for making authenticated API requests
export function useApi() {
  const { token } = useAuth()

  // Generic fetch function with authentication and token retry logic
  const fetchWithAuth = async <T,>(url: string, options: RequestInit = {}): Promise<T> => {
    const headers = new Headers(options.headers)

    // Add authorization header if token exists
    if (token) {
      headers.set("Authorization", `Bearer ${token}`)
    }

    // Apply base path if the URL is relative and doesn't start with http or //
    let finalUrl = url
    if (!url.startsWith("http") && !url.startsWith("//")) {
      finalUrl = getBasePathUrl(url)
    }

    try {
      const response = await fetch(finalUrl, {
        ...options,
        headers,
      })

      if (!response.ok) {
        // Handle 401 Unauthorized errors
        if (response.status === 401) {
          console.warn("Received 401 Unauthorized from", finalUrl)

          // Only request a new token if we haven't already requested one
          if (tokenManager.shouldRequestToken()) {
            console.log("Requesting new token from parent")
            requestTokenDueToAuthFailure(`401 Unauthorized from ${finalUrl}`)
          }

          // Queue this request to be retried when we get a new token
          console.log("Queueing request for retry when new token is received")
          const retriedResponse = await tokenManager.queueRequest(finalUrl, {
            ...options,
            headers,
          })

          // Process the retried response
          if (!retriedResponse.ok) {
            const errorData = await retriedResponse.json().catch(() => ({}))
            throw new Error(errorData.message || `API request failed with status ${retriedResponse.status}`)
          }

          // For non-JSON responses, return the response object
          const contentType = retriedResponse.headers.get("content-type")
          if (contentType && contentType.includes("application/json")) {
            return retriedResponse.json()
          }

          return retriedResponse as unknown as T
        }

        // Handle other error responses
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.message || `API request failed with status ${response.status}`)
      }

      // For non-JSON responses, return the response object
      const contentType = response.headers.get("content-type")
      if (contentType && contentType.includes("application/json")) {
        return response.json()
      }

      return response as unknown as T
    } catch (error) {
      // Rethrow the error
      throw error
    }
  }

  // Helper methods for common HTTP methods
  const get = <T,>(endpoint: string, options: RequestInit = {}) =>
    fetchWithAuth<T>(endpoint, { ...options, method: "GET" })

  const post = <T,>(endpoint: string, data: any, options: RequestInit = {}) =>
    fetchWithAuth<T>(endpoint, {
      ...options,
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...options.headers,
      },
      body: JSON.stringify(data),
    })

  const put = <T,>(endpoint: string, data: any, options: RequestInit = {}) =>
    fetchWithAuth<T>(endpoint, {
      ...options,
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        ...options.headers,
      },
      body: JSON.stringify(data),
    })

  const del = <T,>(endpoint: string, options: RequestInit = {}) =>
    fetchWithAuth<T>(endpoint, { ...options, method: "DELETE" })

  // Create full URLs for each API
  const createUrl = (baseUrl: string, path: string) => {
    // Remove trailing slash from base URL if present
    const base = baseUrl.endsWith("/") ? baseUrl.slice(0, -1) : baseUrl
    // Remove leading slash from path if present
    const cleanPath = path.startsWith("/") ? path.slice(1) : path
    return `${base}/${cleanPath}`
  }

  // API-specific methods
  const mainApi = {
    get: <T,>(path: string, options?: RequestInit) => get<T>(createUrl(API_ENDPOINTS.MAIN.BASE_URL, path), options),
    post: <T,>(path: string, data: any, options?: RequestInit) =>
      post<T>(createUrl(API_ENDPOINTS.MAIN.BASE_URL, path), data, options),
    put: <T,>(path: string, data: any, options?: RequestInit) =>
      put<T>(createUrl(API_ENDPOINTS.MAIN.BASE_URL, path), data, options),
    delete: <T,>(path: string, options?: RequestInit) => del<T>(createUrl(API_ENDPOINTS.MAIN.BASE_URL, path), options),
  }

  const adminApi = {
    get: <T,>(path: string, options?: RequestInit) => get<T>(createUrl(API_ENDPOINTS.ADMIN.BASE_URL, path), options),
    post: <T,>(path: string, data: any, options?: RequestInit) =>
      post<T>(createUrl(API_ENDPOINTS.ADMIN.BASE_URL, path), data, options),
    put: <T,>(path: string, data: any, options?: RequestInit) =>
      put<T>(createUrl(API_ENDPOINTS.ADMIN.BASE_URL, path), data, options),
    delete: <T,>(path: string, options?: RequestInit) => del<T>(createUrl(API_ENDPOINTS.ADMIN.BASE_URL, path), options),
  }

  return {
    mainApi,
    adminApi,
    fetchWithAuth,
  }
}

/**
 * Hook to get labour burden calculation API functionality
 */
export function useLabourBurdenApi() {
  const { mainApi } = useApi()

  const calculateLabourBurden = async (
    formData: CalculatorFormData,
    provinceData: CalculatorProvince,
    organizationId?: number
  ): Promise<{
    success: boolean;
    data?: CostCalculationResult;
    error?: string;
  }> => {
    try {
      const salaryValue = parseFloat(formData.salary) || 0;
      
      const requestBody = {
        salary: salaryValue,
        taxSubdivisionCode: provinceData.code, // Use the subdivision code directly (already in CA-XX format)
        organizationId: organizationId || null,
        wcbCode: formData.wsibCode || '',
        wcbRateOverride: formData.wsibRateOverride ? parseFloat(formData.wsibRateOverride) : null,
        applyCpp: formData.applyCpp,
        applyQpp: formData.applyQpp,
        applyEi: formData.applyEi,
        vacationDays: parseInt(formData.vacationDays) || 0,
        sickDays: parseInt(formData.sickDays) || 0,
        numPublicHolidays: parseInt(formData.numPublicHolidays) || 0,
        otherPaidLeave: parseInt(formData.otherPaidLeave) || 0,
        benefits: parseFloat(formData.benefits) || 0,
        bonusesIncentives: parseFloat(formData.bonusesIncentives) || 0,
        trainingOverhead: parseFloat(formData.trainingOverhead) || 0,
        // Respect user's EHT checkbox choice
        includeEHT: formData.includeEHT,
        annualPayroll: salaryValue, // Always use salary for EHT calculation
        hoursPerDay: parseFloat(formData.hoursPerDay) || 8
      };

      const result = await mainApi.post<any>('/api/Payroll/calculateLabourBurdenUnified', requestBody);

      if (!result.success) {
        throw new Error(result.error || 'Calculation failed');
      }

      // Convert API response to match local CostCalculationResult format
      const costResult: CostCalculationResult = {
        basePay: result.breakdown.basePay,
        cppQppContribution: result.breakdown.cppQppContribution,
        pensionType: result.breakdown.pensionType,
        pension1Contribution: result.breakdown.pension1Contribution,
        pension2Contribution: result.breakdown.pension2Contribution,
        eiContribution: result.breakdown.eiContribution,
        wsibContribution: result.breakdown.wsibContribution,
        ehtContribution: result.breakdown.ehtContribution,
        benefits: result.breakdown.benefits,
        bonusesIncentives: result.breakdown.bonusesIncentives,
        trainingOverhead: result.breakdown.trainingOverhead,
        total: result.breakdown.total,
        totalPaidTimeOffDays: result.breakdown.totalPaidTimeOffDays,
        productiveHours: result.breakdown.productiveHours,
        effectiveHourlyRate: result.effectiveHourlyRate,
        standardHourlyRate: result.breakdown.standardHourlyRate,
        burdenedHourlyRate: result.breakdown.burdenedHourlyRate
      };



      return {
        success: true,
        data: costResult
      };

    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error occurred'
      };
    }
  };

  return {
    calculateLabourBurden
  };
}
